<template>
  <div class="file-uploader-container">
    <div
      :class="['dropzone-area rounded-lg pa-6 border border-dashed text-center cursor-pointer transition-all', isDragOver ? 'border-primary bg-primary-lighten-5' : 'bg-grey-lighten-5']"
      @dragover.prevent="isDragOver = true"
      @dragleave.prevent="isDragOver = false"
      @drop.prevent="handleFileDrop"
      @click="fileInputRef?.click()"
    >
      <v-icon size="40" :color="isDragOver ? 'primary' : 'grey-darken-1'" class="mb-2">
        mdi-file-upload-outline
      </v-icon>
      <div class="text-body-2 font-weight-bold text-grey-darken-3">
        Перетащите сюда файлы или нажмите для выбора
      </div>
      <div class="text-caption text-grey mt-1">Допускаются документы PDF, DOCX, XLSX, PPTX (до 25 МБ)</div>

      <!-- Скрытый инпут -->
      <input
        type="file"
        ref="fileInputRef"
        multiple
        class="d-none"
        @change="handleFileSelect"
      />
    </div>

    <!-- Список загружаемых и уже загруженных файлов -->
    <v-list density="compact" class="bg-transparent mt-3 pa-0" v-if="uploadingFiles.length > 0">
      <v-card
        v-for="file in uploadingFiles"
        :key="file.id"
        variant="flat"
        class="mb-2 pa-2 rounded-md border bg-white"
      >
        <div class="d-flex align-center justify-space-between">
          <div class="d-flex align-center overflow-hidden flex-grow-1 mr-2">
            <v-icon :color="getFileIconColor(file.name)" class="mr-2">
              {{ getFileIcon(file.name) }}
            </v-icon>
            <div class="overflow-hidden">
              <div class="text-caption font-weight-bold text-truncate text-grey-darken-4" v-text="file.name"></div>
              <v-progress-linear
                v-if="file.status === 'loading'"
                :model-value="file.progress"
                height="4"
                color="success"
                rounded
                class="mt-1"
              ></v-progress-linear>
            </div>
          </div>

          <!-- Кнопка действия (Удалить / Отмена) -->
          <v-btn
            icon="mdi-delete-outline"
            size="x-small"
            variant="text"
            color="error"
            @click.stop="removeFile(file.id)"
          ></v-btn>
        </div>
      </v-card>
    </v-list>
  </div>
</template>
<script setup>
import { ref, defineEmits, defineProps} from 'vue'
import { customAxios } from "@/service/customAxios";

const emit = defineEmits(['files-updated'])
// Определение входящих параметров (Props) и событий (Emits)
const props = defineProps({
  eventId: { type: Number, required: true }
})
const fileInputRef = ref(null)
const isDragOver = ref(false)
const uploadingFiles = ref([]) // Локальный массив для отслеживания состояний загрузки

// Генерация уникального ID для UI-элементов списка
const generateUiId = () => Math.random().toString(36).substring(2, 9)

// 📤 Обработка файлов, выбранных через проводник
const handleFileSelect = (event) => {
  const files = event.target.files
  if (!files || files.length === 0) return
  uploadQueue(Array.from(files))
  if (fileInputRef.value) fileInputRef.value.value = '' // Сброс инпута
}

// 🧲 Обработка файлов, сброшенных через Drag-and-drop
const handleFileDrop = (event) => {
  isDragOver.value = false
  const files = event.dataTransfer?.files
  if (!files || files.length === 0) return
  uploadQueue(Array.from(files))
}

// 🔄 Добавление файлов в очередь асинхронной загрузки
const uploadQueue = (filesList) => {
  filesList.forEach(file => {
    const fileItem = {
      id: generateUiId(),

      dbId: null, // Сюда запишем ID из базы данных после ответа сервера
      name: file.name,
      progress: 0,
      status: 'loading'
    }
    uploadingFiles.value.push(fileItem)
    sendFileToServer(file, fileItem)
  })
}

// 📡 Отправка одиночного файла через FormData на бэкенд /api/v1/files/upload/
const sendFileToServer = async (file, fileItem) => {
  const formData = new FormData()
  formData.append('file', file)

  try {
    const response = await customAxios.post('/v1/files/upload/' +  props.eventId, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total) {
          fileItem.progress = Math.round((progressEvent.loaded / progressEvent.total) * 100)
        }
      }
    })

    // Файл успешно сохранен на сервере и в БД
    if (response.status === 201) {
      fileItem.status = 'success'
      fileItem.dbId = response.data.id
      notifyParent()
    }
  } catch (error) {
    console.error(`Ошибка при загрузке файла ${fileItem.name}:`, error)
    fileItem.status = 'error'
    fileItem.progress = 0
  }
}

// Удаление файла из списка (или отмена загрузки)
const removeFile = (uiId) => {
  uploadingFiles.value = uploadingFiles.value.filter(f => f.id !== uiId)
  notifyParent()
}

// Передача наверх массива ID файлов из БД, готовых к привязке к занятию/материалу
const notifyParent = () => {
  const successIds = uploadingFiles.value
    .filter(f => f.status === 'success' && f.dbId)
    .map(f => f.dbId)
  emit('files-updated', successIds)
}

// 📂 Хелпер для динамического подбора иконки по расширению
const getFileIcon = (fileName) => {
  const ext = fileName.split('.').pop()?.toLowerCase()
  switch (ext) {
    case 'pdf': return 'mdi-file-pdf-box'
    case 'doc':
    case 'docx': return 'mdi-file-word-box'
    case 'xls':
    case 'xlsx': return 'mdi-file-excel-box'
    case 'ppt':
    case 'pptx': return 'mdi-file-powerpoint-box'
    default: return 'mdi-file-document-outline'
  }
}

// 🎨 Хелпер для цвета иконки расширения
const getFileIconColor = (fileName) => {
  const ext = fileName.split('.').pop()?.toLowerCase()
  switch (ext) {
    case 'pdf': return 'red-darken-1'
    case 'doc':
    case 'docx': return 'blue-darken-2'
    case 'xls':
    case 'xlsx': return 'green-darken-2'
    case 'ppt':
    case 'pptx': return 'orange-darken-2'
    default: return 'grey-darken-2'
  }
}


</script>

<style scoped>
/* Стилизация области Drag-and-Drop зоны */
.dropzone-area {
  border-width: 2px !important;
  border-style: dashed !important;
  border-color: rgba(0, 0, 0, 0.15) !important;
  transition: all 0.2s ease-in-out;
}

.dropzone-area:hover {
  border-color: var(--v-theme-primary) !important;
  background-color: rgba(var(--v-theme-primary), 0.02) !important;
}

.transition-all {
  transition: all 0.2s ease-in-out;
}

.cursor-pointer {
  cursor: pointer;
}

.text-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>